import React from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { SceneBase } from './SceneBase';
import { PoolBall } from '../atoms/snooker/PoolBall';
import { PoolCue } from '../atoms/snooker/PoolCue';
import { roll, rollReduced } from '../../../../../theme/animations';

/**
 * SnookerScene - Card background for snooker venue type.
 *
 * Features a rich green baize gradient, subtle cloth texture pattern,
 * cushion rail inset border, and decorative snooker atoms.
 * Hover deepens the baize effect.
 */
export const SnookerScene = React.memo(function SnookerScene() {
  const theme = useTheme();
  const snookerColor = theme.palette.venue.snooker;

  return (
    <SceneBase>
      {({ isMobile, shouldReduceMotion }) => (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            // Rich green baize gradient
            background: `linear-gradient(135deg, ${snookerColor}1F 0%, ${snookerColor}0A 100%)`,
            // Baize cloth texture - fine diagonal lines at very low opacity
            backgroundImage: `
              linear-gradient(135deg, ${snookerColor}1F 0%, ${snookerColor}0A 100%),
              repeating-linear-gradient(
                45deg,
                transparent,
                transparent 3px,
                ${snookerColor}05 3px,
                ${snookerColor}05 4px
              ),
              repeating-linear-gradient(
                -45deg,
                transparent,
                transparent 3px,
                ${snookerColor}05 3px,
                ${snookerColor}05 4px
              )
            `,
            // Cushion rail inset border
            boxShadow: `inset 0 0 0 2px ${snookerColor}26, inset 0 0 20px ${snookerColor}0D`,
            // Hover intensification - CSS only, hover-capable devices only
            transition: 'box-shadow 0.3s ease, background 0.3s ease',
            '@media (hover: hover)': {
              '&:hover': {
                boxShadow: `inset 0 0 0 2px ${snookerColor}40, inset 0 0 30px ${snookerColor}1A`,
                background: `linear-gradient(135deg, ${snookerColor}2B 0%, ${snookerColor}12 100%)`,
              },
            },
          }}
        >
          {/* PoolCue atom - positioned diagonally behind the ball */}
          <motion.div
            variants={shouldReduceMotion ? rollReduced : undefined}
            initial={shouldReduceMotion ? 'hidden' : undefined}
            animate={shouldReduceMotion ? 'visible' : undefined}
            style={{
              position: 'absolute',
              bottom: isMobile ? '12%' : '14%',
              right: isMobile ? '14%' : '16%',
              opacity: 0.2,
              transform: 'rotate(-20deg)',
            }}
          >
            <PoolCue size={isMobile ? 48 : 64} />
          </motion.div>

          {/* PoolBall atom - bottom right, in front of cue */}
          <motion.div
            variants={shouldReduceMotion ? rollReduced : roll}
            initial="hidden"
            animate="visible"
            style={{
              position: 'absolute',
              bottom: isMobile ? '8%' : '10%',
              right: isMobile ? '6%' : '8%',
              opacity: 0.3,
            }}
          >
            <PoolBall size={isMobile ? 36 : 48} number={6} />
          </motion.div>

          {/* Second ball - upper left area (desktop only) */}
          {!isMobile && (
            <motion.div
              variants={shouldReduceMotion ? rollReduced : roll}
              initial="hidden"
              animate="visible"
              style={{
                position: 'absolute',
                top: '12%',
                left: '10%',
                opacity: 0.15,
              }}
            >
              <PoolBall size={28} number={3} />
            </motion.div>
          )}
        </Box>
      )}
    </SceneBase>
  );
});
